import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Check, MessageSquareText, Pin, Trash2, X } from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { communitiesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import "./CommunityBoard.css";

const CommunityBoard = () => {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToasts();
  const communitiesSnapshot = location.state?.fromCommunitiesSnapshot || null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [community, setCommunity] = useState(null);
  const [board, setBoard] = useState({
    title: "",
    body: "",
    updatedAt: null,
    hasContent: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");

  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      {
        label: "Communities",
        path: communitiesSnapshot ? null : "/communities",
        onClick: communitiesSnapshot
          ? () => navigate("/communities", { state: communitiesSnapshot })
          : undefined,
      },
      {
        label: community?.name || "Community",
        path: community ? `/communities/${communityId}` : null,
      },
      { label: "Board", path: null },
    ],
    [communitiesSnapshot, community, communityId, navigate],
  );

  useEffect(() => {
    const loadBoard = async () => {
      try {
        setLoading(true);
        const response = await communitiesAPI.getCommunityBoard(communityId);
        setCommunity(response.data.community);
        setBoard(response.data.board);
        setDraftTitle(response.data.board?.title || "");
        setDraftBody(response.data.board?.body || "");
        setIsEditing(false);
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to load board",
          "error-warning",
        );
        navigate("/communities");
      } finally {
        setLoading(false);
      }
    };

    loadBoard();
  }, [addToast, communityId, navigate]);

  const handleStartEdit = () => {
    setDraftTitle(board.title || "");
    setDraftBody(board.body || "");
    setIsConfirmingReset(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraftTitle(board.title || "");
    setDraftBody(board.body || "");
    setIsConfirmingReset(false);
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await communitiesAPI.updateCommunityBoard(communityId, {
        boardTitle: draftTitle,
        boardBody: draftBody,
      });
      setBoard(response.data.board);
      setIsConfirmingReset(false);
      setIsEditing(false);
      addToast("Board updated", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update board",
        "error-warning",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleResetBoard = async () => {
    try {
      setSaving(true);
      const response = await communitiesAPI.updateCommunityBoard(communityId, {
        boardTitle: "",
        boardBody: "",
      });
      setBoard(response.data.board);
      setDraftTitle("");
      setDraftBody("");
      setIsConfirmingReset(false);
      setIsEditing(false);
      addToast("Board cleared", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to clear board",
        "error-warning",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner visible={true} />;
  }

  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
      buttons={
        <NavigationButtons
          hideActiveButton={true}
          communityRouteState={communitiesSnapshot}
        />
      }
    >
      <div className="community-board-page">
        <section className="community-board-hero">
          <div className="community-board-header">
            <div className="community-board-title-group">
              <span className="community-board-title-icon">
                <Pin size={16} />
              </span>
              <h1>Board</h1>
              {community?.canManage ? (
                <>
                  <Button
                    className={`community-board-action-button community-board-action-button-board ${
                      isEditing ? "community-board-action-button-danger" : ""
                    }`}
                    onClick={isEditing ? handleCancelEdit : handleStartEdit}
                    title={isEditing ? "Cancel" : board.hasContent ? "Edit board" : "Create board"}
                    aria-label={isEditing ? "Cancel" : board.hasContent ? "Edit board" : "Create board"}
                  >
                    {isEditing ? <X size={16} /> : <MessageSquareText size={16} />}
                  </Button>
                  {board.hasContent && !isEditing ? (
                    isConfirmingReset ? (
                      <>
                        <Button
                          className="community-board-action-button"
                          onClick={() => setIsConfirmingReset(false)}
                          disabled={saving}
                          title="Cancel clear"
                          aria-label="Cancel clear"
                        >
                          <X size={16} />
                        </Button>
                        <Button
                          className="community-board-action-button community-board-action-button-success"
                          onClick={handleResetBoard}
                          disabled={saving}
                          title="Confirm clear"
                          aria-label="Confirm clear"
                        >
                          <Check size={16} />
                        </Button>
                      </>
                    ) : (
                      <Button
                        className="community-board-action-button community-board-action-button-danger"
                        onClick={() => setIsConfirmingReset(true)}
                        disabled={saving}
                        title="Clear board"
                        aria-label="Clear board"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )
                  ) : null}
                  {isEditing ? (
                    <Button
                      className="community-board-action-button community-board-action-button-success"
                      onClick={handleSave}
                      disabled={saving}
                      title="Save"
                      aria-label="Save"
                    >
                      <Check size={16} />
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </section>

        <section className="community-board-card">
          {isEditing ? (
            <div className="community-board-edit">
              <input
                type="text"
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.target.value)}
                placeholder="Board title"
                className="community-board-input"
                maxLength="80"
              />
              <textarea
                value={draftBody}
                onChange={(event) => setDraftBody(event.target.value)}
                placeholder="Write the message your members should see here."
                className="community-board-textarea"
                rows="12"
                maxLength="5000"
              />
            </div>
          ) : board.hasContent ? (
            <div className="community-board-content">
              {board.title || board.updatedAt || board.body ? (
                <div className="community-board-post">
                  <div className="community-board-post-header">
                    <span className="community-board-post-icon">
                      <MessageSquareText size={16} />
                    </span>
                    <div className="community-board-content-header">
                      {board.title ? <h2>{board.title}</h2> : null}
                      {board.updatedAt ? (
                        <p className="community-board-updated">
                          Updated {new Date(board.updatedAt).toLocaleString()}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="community-board-body">
                {board.body.split("\n").map((line, index) => (
                  <p key={`${index}-${line}`}>{line || "\u00A0"}</p>
                ))}
              </div>
            </div>
          ) : (
            <div className="community-board-empty">
              <h2>No board message yet</h2>
              <p>
                {community?.canManage
                  ? "Create the first pinned message for your members."
                  : "The owner has not posted anything yet."}
              </p>
            </div>
          )}
        </section>
      </div>
    </StudyLayout>
  );
};

export default CommunityBoard;
